import React from 'react';
import { FiCheckCircle, FiAlertCircle, FiTrendingUp, FiTrendingDown, FiInfo, FiMessageSquare, FiChevronRight, 
         FiDownload, FiMapPin, FiDollarSign, FiUser, FiCalendar } from 'react-icons/fi';
import IconWrapper from './IconWrapper';
import { useFramework } from '../contexts/FrameworkContext';

interface ClosureReportProps {
  reportId: string;
  onClose: () => void;
}

const ClosureReport: React.FC<ClosureReportProps> = ({ reportId, onClose }) => {
  const { framework } = useFramework();
  
  // Mock data for the closure report
  const reportData = {
    id: reportId,
    title: 'EveryDay Account Closure Report',
    date: 'April 3, 2025',
    productName: 'EveryDay Account',
    productPeriod: 'January 2025 - March 2025',
    summaryStats: {
      totalComplaints: 47,
      resolvedComplaints: 42,
      openComplaints: 5,
      avgResolutionTime: '3.2 days',
      complaintsTrend: -8.5, // negative means decrease
      financialImpact: 'R12,450',
      customerSatisfaction: '84%'
    },
    key_insights: [
      {
        title: 'Significant reduction in fee-related complaints',
        description: 'Complaints about account fees have decreased by 32% since the new fee structure was implemented in January.',
        sentiment: 'positive',
      },
      {
        title: 'Mobile app access issues persist',
        description: 'Despite improvements, 28% of all complaints are still related to mobile app access problems, particularly during high-traffic periods.',
        sentiment: 'negative',
      },
      {
        title: 'Customer service response time improved',
        description: 'Average customer service response time decreased from 24 hours to 8 hours, resulting in higher satisfaction scores.',
        sentiment: 'positive',
      },
      {
        title: 'Unclear terms in marketing materials',
        description: 'Several complaints highlight confusion about promotional terms, particularly regarding the "zero fees" marketing campaign.',
        sentiment: 'negative',
      },
    ],
    root_cause_analysis: {
      primary_causes: [
        {
          cause: 'App Server Capacity',
          percentage: 28,
          description: 'Insufficient server capacity during peak usage hours leading to app crashes and timeouts',
          impact: 'High customer frustration and potential transaction delays',
          related_complaints: 13
        },
        {
          cause: 'Unclear Fee Disclosure',
          percentage: 23,
          description: 'Marketing materials do not clearly state exceptions to the "zero fees" policy',
          impact: 'Customer confusion and feeling of deception',
          related_complaints: 11
        },
        {
          cause: 'Limited Weekend Support',
          percentage: 17,
          description: 'Reduced customer support staff availability during weekends',
          impact: 'Extended resolution times for weekend-reported issues',
          related_complaints: 8
        },
        {
          cause: 'Notification Process Gaps',
          percentage: 15,
          description: 'Lack of proactive communication about scheduled maintenance',
          impact: 'Unexpected service disruptions for customers',
          related_complaints: 7
        },
        {
          cause: 'Other',
          percentage: 17,
          description: 'Various miscellaneous issues',
          impact: 'Various impacts',
          related_complaints: 8
        }
      ],
      recurrence_risk: 'Medium',
      ai_confidence: 92
    },
    framework_compliance: {
      ncd: {
        overallScore: 87,
        categoriesScores: [
          { name: 'Consumer Understanding', score: 72, issues: 8 },
          { name: 'Products and Services', score: 94, issues: 2 },
          { name: 'Price and Value', score: 91, issues: 3 },
          { name: 'Consumer Support', score: 86, issues: 7 },
        ],
        improvement_areas: [
          'Consumer Understanding - Marketing materials clarity',
          'Consumer Support - Weekend response time',
        ],
      },
      tcf: {
        overallScore: 83,
        categoriesScores: [
          { name: 'Outcome 1: Culture & Governance', score: 92, issues: 1 },
          { name: 'Outcome 2: Product Design', score: 88, issues: 3 },
          { name: 'Outcome 3: Clear Information', score: 68, issues: 9 },
          { name: 'Outcome 4: Suitable Advice', score: 90, issues: 2 },
          { name: 'Outcome 5: Product Performance', score: 85, issues: 5 },
          { name: 'Outcome 6: No Barriers', score: 89, issues: 3 },
        ],
        improvement_areas: [
          'Outcome 3: Clear Information - Promotional material clarity',
          'Outcome 5: Product Performance - Mobile app stability',
        ],
      }
    },
    financial_analysis: {
      total_impact: 'R12,450',
      breakdown: [
        { category: 'Fee Refunds', amount: 'R7,250', percentage: 58 },
        { category: 'Goodwill Gestures', amount: 'R3,200', percentage: 26 },
        { category: 'Transaction Reversals', amount: 'R2,000', percentage: 16 }
      ],
      compliance_implications: 'Low - No regulatory reporting thresholds exceeded',
      average_per_complaint: 'R264.89'
    },
    regulatory_assessment: {
      fsca_compliance: {
        status: 'Compliant',
        notable_items: [
          'All complaints resolved within required timeframes',
          'Proper documentation and record-keeping maintained',
          'Two complaints required notification to Treating Customers Fairly (TCF) officer'
        ]
      },
      sarb_compliance: {
        status: 'Compliant',
        notable_items: [
          'No systemic issues identified requiring formal notification',
          'Financial impact below reportable threshold',
          'Customer communication for all affected parties completed'
        ]
      },
      reporting_requirements: 'Include in quarterly FSCA submission due April 15, 2025',
      next_steps: 'Schedule internal audit review of app reliability procedures'
    },
    verbatim_samples: [
      {
        id: 'V-0042',
        content: "I've been trying to access my account through the app for two days now but keep getting error messages during peak hours. This is really frustrating when I need to make urgent transfers.",
        category: framework === 'NCD' ? 'Consumer Support' : 'Outcome 5: Product Performance',
        date: 'March 15, 2025',
        branch: 'Johannesburg CBD',
        channel: 'Mobile App'
      },
      {
        id: 'V-0053',
        content: "The new fee structure is so much better! I'm no longer being charged for transfers between my accounts. This makes managing my finances much easier.",
        category: framework === 'NCD' ? 'Price and Value' : 'Outcome 2: Product Design',
        date: 'March 22, 2025',
        branch: 'Online Only',
        channel: 'Email'
      },
      {
        id: 'V-0061',
        content: "The EveryDay Account promotion advertised 'zero fees' but I'm still being charged for international transfers. This wasn't clearly explained in any of the materials I received when opening my account.",
        category: framework === 'NCD' ? 'Consumer Understanding' : 'Outcome 3: Clear Information',
        date: 'March 27, 2025',
        branch: 'Cape Town Central',
        channel: 'Branch Visit'
      },
    ],
    recommendations: [
      {
        title: 'Review and clarify marketing materials',
        description: 'Ensure all promotional materials clearly state exceptions to the "zero fees" policy, particularly regarding international transfers.',
        priority: 'High',
        owner: 'Marketing Team',
        timeline: '30 days',
        tcf_alignment: 'Outcome 3: Clear Information'
      },
      {
        title: 'Increase app server capacity during peak hours',
        description: 'Allocate additional resources to handle increased traffic during peak usage hours (8-9 AM and 5-7 PM on weekdays).',
        priority: 'High',
        owner: 'IT Operations',
        timeline: '14 days',
        tcf_alignment: 'Outcome 5: Product Performance'
      },
      {
        title: 'Enhance customer support during weekends',
        description: 'Increase customer support staffing during weekend hours to maintain the improved response times across all days.',
        priority: 'Medium',
        owner: 'Customer Support',
        timeline: '60 days',
        tcf_alignment: 'Outcome 6: No Barriers'
      },
      {
        title: 'Implement proactive communication for app maintenance',
        description: 'Send push notifications and emails before scheduled maintenance to reduce unexpected access issues.',
        priority: 'Medium',
        owner: 'IT Operations & Communications',
        timeline: '45 days',
        tcf_alignment: 'Outcome 3: Clear Information'
      },
    ],
  };
  
  const getComplianceScore = () => {
    if (framework === 'NCD') {
      return reportData.framework_compliance.ncd;
    } else {
      return reportData.framework_compliance.tcf;
    }
  };
  
  const complianceData = getComplianceScore();
  
  const getSentimentIcon = (sentiment: string) => {
    switch(sentiment) {
      case 'positive':
        return <IconWrapper icon={FiCheckCircle} className="text-green-500" size={20} />;
      case 'negative':
        return <IconWrapper icon={FiAlertCircle} className="text-red-500" size={20} />;
      default:
        return <IconWrapper icon={FiInfo} className="text-blue-500" size={20} />;
    }
  };
  
  const getPriorityClass = (priority: string) => {
    switch(priority) {
      case 'High':
        return 'bg-red-100 text-red-800';
      case 'Medium':
        return 'bg-amber-100 text-amber-800';
      case 'Low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
        <div>
          <h2 className="text-2xl font-bold">{reportData.title}</h2>
          <p className="text-secondary/70">Generated on {reportData.date} • Product: {reportData.productName}</p>
        </div>
        <button 
          className="px-4 py-2 border border-gray-200 rounded-lg text-sm"
          onClick={onClose}
        >
          Close Report
        </button>
      </div>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-secondary/70">Total Complaints</div>
          <div className="text-2xl font-bold">{reportData.summaryStats.totalComplaints}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-secondary/70">Resolved</div>
          <div className="text-2xl font-bold text-green-600">{reportData.summaryStats.resolvedComplaints}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-secondary/70">Open</div>
          <div className="text-2xl font-bold text-amber-600">{reportData.summaryStats.openComplaints}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-secondary/70">Avg. Resolution Time</div>
          <div className="text-2xl font-bold">{reportData.summaryStats.avgResolutionTime}</div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-secondary/70">Trend vs. Previous</div>
          <div className="text-2xl font-bold flex items-center">
            {reportData.summaryStats.complaintsTrend < 0 ? (
              <>
                <span className="text-green-600">{reportData.summaryStats.complaintsTrend}%</span>
                <IconWrapper icon={FiTrendingDown} className="ml-1 text-green-600" size={20} />
              </>
            ) : (
              <>
                <span className="text-red-600">+{reportData.summaryStats.complaintsTrend}%</span>
                <IconWrapper icon={FiTrendingUp} className="ml-1 text-red-600" size={20} />
              </>
            )}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-secondary/70">Financial Impact</div>
          <div className="text-2xl font-bold">{reportData.summaryStats.financialImpact}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-secondary/70">Customer Satisfaction</div>
          <div className="text-2xl font-bold text-primary">{reportData.summaryStats.customerSatisfaction}</div>
        </div>
      </div>
      
      {/* Key Insights */}
      <div className="mb-8">
        <h3 className="text-xl font-bold mb-4">Key Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reportData.key_insights.map((insight, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="mt-1 mr-3">
                  {getSentimentIcon(insight.sentiment)}
                </div>
                <div>
                  <h4 className="font-medium">{insight.title}</h4>
                  <p className="text-sm text-secondary/70 mt-1">{insight.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Compliance Scores */}
      <div className="mb-8">
        <h3 className="text-xl font-bold mb-4">
          {framework === 'NCD' ? 'NCD Compliance' : 'TCF Compliance'}
        </h3>
        
        <div className="flex items-center mb-4 p-4 bg-primary/5 rounded-lg">
          <div className="w-20 h-20 rounded-full border-4 border-primary flex items-center justify-center mr-6">
            <span className="text-2xl font-bold">{complianceData.overallScore}%</span>
          </div>
          <div>
            <div className="font-medium text-lg">Overall Compliance Score</div>
            <p className="text-sm text-secondary/70">
              Based on complaint analysis for {reportData.productPeriod}
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {complianceData.categoriesScores.map((category, index) => (
            <div key={index} className="border border-gray-100 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <div className="font-medium">{category.name}</div>
                <div className="text-sm">
                  <span 
                    className={`${
                      category.score >= 90 ? 'text-green-600' : 
                      category.score >= 80 ? 'text-blue-600' :
                      category.score >= 70 ? 'text-amber-600' :
                      'text-red-600'
                    }`}
                  >
                    {category.score}%
                  </span>
                  <span className="text-secondary/50 ml-2">({category.issues} issues)</span>
                </div>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div 
                  className={`h-full rounded-full ${
                    category.score >= 90 ? 'bg-green-500' : 
                    category.score >= 80 ? 'bg-blue-500' :
                    category.score >= 70 ? 'bg-amber-500' :
                    'bg-red-500'
                  }`}
                  style={{ width: `${category.score}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mb-4">
          <h4 className="font-medium mb-2">Improvement Areas</h4>
          <ul className="list-disc list-inside text-sm text-secondary/70 space-y-1">
            {complianceData.improvement_areas.map((area, index) => (
              <li key={index}>{area}</li>
            ))}
          </ul>
        </div>
      </div>
      
      {/* Sample Verbatims */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Sample Verbatims</h3>
          <button className="text-primary text-sm flex items-center hover:underline">
            View All Verbatims
            <IconWrapper icon={FiChevronRight} className="ml-1" size={14} />
          </button>
        </div>
        
        <div className="space-y-4">
          {reportData.verbatim_samples.map((verbatim, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3 flex-shrink-0">
                  <IconWrapper icon={FiMessageSquare} size={16} />
                </div>
                <div className="flex-1">
                  <p className="text-sm mb-3">{verbatim.content}</p>
                  <div className="flex flex-wrap justify-between items-center">
                    <div className="text-xs text-secondary/70">
                      ID: {verbatim.id} • {verbatim.date}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-secondary/70">
                        <IconWrapper icon={FiMapPin} className="inline mr-1" size={12} />
                        {verbatim.branch}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-full">
                        {verbatim.channel}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-primary/10 rounded-full">
                        {verbatim.category}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Root Cause Analysis */}
      <div className="mb-8">
        <h3 className="text-xl font-bold mb-4">Root Cause Analysis</h3>
        <div className="bg-primary/5 rounded-lg p-5 border border-primary/10">
          <div className="flex justify-between items-center mb-4">
            <div className="text-base font-medium">Primary Causes</div>
            <div className="text-sm flex items-center">
              AI Confidence: 
              <span className="ml-1 text-primary font-semibold">{reportData.root_cause_analysis.ai_confidence}%</span>
            </div>
          </div>
          
          <div className="space-y-3 mb-6">
            {reportData.root_cause_analysis.primary_causes.map((cause, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-100 p-3">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-medium">{cause.cause}</h4>
                  <span className="text-sm font-semibold px-2 py-0.5 bg-primary/10 rounded-full">
                    {cause.percentage}% of complaints
                  </span>
                </div>
                <div className="text-sm text-secondary/70 mb-1">{cause.description}</div>
                <div className="flex justify-between text-xs">
                  <span className="text-red-600">Impact: {cause.impact}</span>
                  <span>Related complaints: {cause.related_complaints}</span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex justify-between p-3 bg-white rounded-lg border border-gray-100">
            <div className="text-sm font-medium">Recurrence Risk Assessment</div>
            <div className="text-sm font-medium text-amber-600">{reportData.root_cause_analysis.recurrence_risk}</div>
          </div>
        </div>
      </div>
      
      {/* Financial Analysis */}
      <div className="mb-8">
        <h3 className="text-xl font-bold mb-4">Financial Impact Analysis</h3>
        <div className="bg-gray-50 rounded-lg p-5">
          <div className="flex justify-between items-center mb-4">
            <div className="text-base font-medium flex items-center">
              <span className="inline-block w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3">
                <IconWrapper icon={FiDollarSign} size={16} />
              </span>
              Total Financial Impact: {reportData.financial_analysis.total_impact}
            </div>
            <div className="text-sm">
              Average per complaint: <span className="font-medium">{reportData.financial_analysis.average_per_complaint}</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {reportData.financial_analysis.breakdown.map((item, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-100 p-3">
                <div className="text-sm text-secondary/70 mb-1">{item.category}</div>
                <div className="flex justify-between items-center">
                  <div className="text-lg font-bold">{item.amount}</div>
                  <div className="text-sm">{item.percentage}%</div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="p-3 bg-white rounded-lg border border-gray-100">
            <div className="text-sm font-medium">Compliance Implications</div>
            <div className="text-sm text-green-600 mt-1">{reportData.financial_analysis.compliance_implications}</div>
          </div>
        </div>
      </div>
      
      {/* Regulatory Assessment */}
      <div className="mb-8">
        <h3 className="text-xl font-bold mb-4">Regulatory Assessment</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium text-blue-800">FSCA Compliance</h4>
              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                {reportData.regulatory_assessment.fsca_compliance.status}
              </span>
            </div>
            <ul className="text-sm space-y-1 text-blue-900">
              {reportData.regulatory_assessment.fsca_compliance.notable_items.map((item, index) => (
                <li key={index} className="flex items-start">
                  <span className="inline-block w-4 h-4 rounded-full bg-blue-200 flex-shrink-0 mr-2 mt-0.5"></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          
          <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium text-indigo-800">SARB Compliance</h4>
              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                {reportData.regulatory_assessment.sarb_compliance.status}
              </span>
            </div>
            <ul className="text-sm space-y-1 text-indigo-900">
              {reportData.regulatory_assessment.sarb_compliance.notable_items.map((item, index) => (
                <li key={index} className="flex items-start">
                  <span className="inline-block w-4 h-4 rounded-full bg-indigo-200 flex-shrink-0 mr-2 mt-0.5"></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium mb-1">Reporting Requirements</div>
              <div className="text-sm">{reportData.regulatory_assessment.reporting_requirements}</div>
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Next Steps</div>
              <div className="text-sm">{reportData.regulatory_assessment.next_steps}</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Recommendations */}
      <div className="mb-8">
        <h3 className="text-xl font-bold mb-4">AI-Generated Recommendations</h3>
        <div className="space-y-4">
          {reportData.recommendations.map((recommendation, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="font-medium">{recommendation.title}</h4>
                  <p className="text-sm text-secondary/70 mt-1">{recommendation.description}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="text-xs bg-gray-100 px-2 py-0.5 rounded flex items-center">
                      <IconWrapper icon={FiUser} className="mr-1" size={12} />
                      Owner: {recommendation.owner}
                    </span>
                    <span className="text-xs bg-gray-100 px-2 py-0.5 rounded flex items-center">
                      <IconWrapper icon={FiCalendar} className="mr-1" size={12} />
                      Timeline: {recommendation.timeline}
                    </span>
                    <span className="text-xs bg-blue-50 text-blue-800 px-2 py-0.5 rounded flex items-center">
                      <IconWrapper icon={FiCheckCircle} className="mr-1" size={12} />
                      {recommendation.tcf_alignment}
                    </span>
                  </div>
                </div>
                <span className={`ml-3 px-2 py-0.5 rounded-full text-xs inline-flex items-center justify-center ${getPriorityClass(recommendation.priority)}`}>
                  {recommendation.priority} Priority
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="mt-8 pt-6 border-t border-gray-100">
        <div className="flex justify-between mb-6">
          <button className="px-4 py-2 border border-gray-200 rounded-lg text-sm flex items-center">
            <IconWrapper icon={FiDownload} className="mr-2" size={16} />
            Download PDF
          </button>
          <button className="px-4 py-2 bg-primary text-secondary rounded-lg text-sm">
            Share Report
          </button>
        </div>
        
        <div className="text-xs text-center text-secondary/60 border-t border-gray-100 pt-4">
          <p>For inquiries or complaints, contact: <span className="font-semibold">complaints@tymebank.co.za</span></p>
          <p className="mt-1">
            Tyme Bank is an authorised financial services provider (FSP 49140) and registered credit provider (NCRCP 10774). 
            Tyme Bank Limited Reg no: 2015/231510/06 • 30 Jellicoe Avenue, Rosebank 2196
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClosureReport;